/**
 * The last line of defence. A render crash anywhere below this boundary lands
 * on a designed page rather than a white screen, and carries enough detail for
 * the person hitting it to paste into a bug report.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { RotateCw, ShieldAlert } from 'lucide-react';

import { Button, CopyButton } from '@/components/ui';

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** Replace the default page — used by narrower boundaries. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
  /** Reported to logging in a real deployment. */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console -- the only place a crash is surfaced to devtools.
    console.error('[AlphaForm] render error', error, info.componentStack);
    this.props.onError?.(error, info);
  }

  private reset = (): void => {
    this.setState({ error: null });
  };

  private reload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(error, this.reset);

    const details = [error.name && `${error.name}: ${error.message}`, error.stack]
      .filter(Boolean)
      .join('\n');

    return (
      <div className="flex h-full w-full items-center justify-center bg-[var(--canvas)] p-8">
        <div className="w-full max-w-[520px] overflow-hidden rounded-[var(--radius-xl)] border border-line bg-surface-1 shadow-[var(--shadow-3)]">
          <div className="flex items-start gap-3 border-b border-line-subtle px-5 py-4">
            <span
              aria-hidden
              className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-[var(--radius-md)] bg-[var(--danger-soft)] text-[var(--danger)]"
            >
              <ShieldAlert className="size-4" />
            </span>
            <div className="min-w-0">
              <h1 className="text-lg font-semibold tracking-[var(--tracking-tight)] text-primary">
                Something broke on this screen
              </h1>
              <p className="mt-1 text-base leading-snug text-muted">
                The interface stopped rendering before it finished. Your data is untouched —
                reloading usually clears it.
              </p>
            </div>
          </div>

          <div className="px-5 py-4">
            <p className="eyebrow">Error</p>
            <pre className="mt-1.5 max-h-40 overflow-auto rounded-[var(--radius-sm)] border border-line-subtle bg-[var(--surface-inset)] px-3 py-2 font-mono text-xs leading-normal text-secondary">
              {details || 'Unknown error'}
            </pre>
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-line-subtle bg-surface-2 px-5 py-3">
            <CopyButton value={details} label="Copy details" withLabel size="md" />
            <Button variant="secondary" onClick={this.reset}>
              Try again
            </Button>
            {/* Recovery, not creation — so not gold. */}
            <Button variant="secondary" iconLeft={<RotateCw />} onClick={this.reload}>
              Reload app
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
